import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { AppVersion, ENV_CONFIG, EnvConfig } from '@core';
import { VersionConfig, VersionConfigStorage } from '../storage/version-config.storage';

@Injectable({ providedIn: 'root' })
export class UpdateService {
  private readonly storage: VersionConfigStorage = inject(VersionConfigStorage);
  private readonly env: EnvConfig = inject(ENV_CONFIG);

  isUpdated(): Observable<boolean> {
    return this.storage.getLoadedConfig$().pipe(
      map((config: VersionConfig): boolean => {
        if (config.availableVersions.includes(AppVersion)) return true;
        // Escape-hatch tylko na produkcji: wersji >= newest_version nie blokujemy
        // (release bez dopisania do listy, buildy testowe). Na stage/local stara logika.
        return this.env.production && isAtLeast(AppVersion, config.newestVersion);
      })
    );
  }

  hasOptionalUpdate(): Observable<boolean> {
    return this.storage
      .getLoadedConfig$()
      .pipe(
        map(
          (config: VersionConfig) =>
            config.availableVersions.includes(AppVersion) && config.newestVersion !== AppVersion
        )
      );
  }

  newestVersion(): Observable<string> {
    return this.storage.getLoadedConfig$().pipe(map((config: VersionConfig) => config.newestVersion));
  }

  availableVersions(): Observable<string[]> {
    return this.storage.getLoadedConfig$().pipe(map((config: VersionConfig) => config.availableVersions));
  }
}

// Wersji równej lub wyższej od newestVersion nie blokujemy - buildy testowe oraz
// release, do którego zapomniano dopisać wersji w available_versions, mają przejść.
function isAtLeast(current: string, newest: string): boolean {
  const a: number[] = current.split('.').map((n: string): number => Number(n) || 0);
  const b: number[] = newest.split('.').map((n: string): number => Number(n) || 0);
  const len: number = Math.max(a.length, b.length);
  for (let i: number = 0; i < len; i++) {
    const diff: number = (a[i] ?? 0) - (b[i] ?? 0);
    if (diff !== 0) return diff > 0;
  }
  return true;
}
